import { useShouldShowInContextAnalysis } from '@modules/analysis/hooks';
import { INITIAL_ANALYSIS } from '@modules/analysis/store/constants';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { FeedIcon } from '@modules/common/components/FeedIcon';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { NAV_PATHS } from '@modules/common/constants';
import { Direction } from '@modules/common/types';
import { DASHBOARD_ACTIVE_CELL_ID } from '@modules/dashboard/constants';
import {
  getAddNewAnalysisToDashboardRoute,
  getDashboardListItemFromDashboardView,
  getEmojiFromDashboard,
} from '@modules/dashboard/helpers';
import {
  useAddMediaWidget,
  useAddTextWidget,
  useIsDashboardEditable,
  useIsMediaWidgetEnabled,
  useUpsertDashboard,
} from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { FeedDrawerContainer } from '@modules/feed/components/FeedDrawerContainer';
import { SAVED_ARTEFACTS_MODAL_ID } from '@modules/leftNavSidebar/components/SavedNavItem/constants';
import { QuantifyDrawerContainer } from '@modules/quantify/components/QuantifyDrawerContainer';
import {
  BookmarkRounded,
  ImageRounded,
  InsightsRounded,
  TextFieldsRounded,
} from '@mui/icons-material';
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  PopoverOrigin,
  SxProps,
  Theme,
} from '@mui/material';
import {
  AnalysisUseCase,
  AnalysisViewFragment,
  Status,
} from 'generated/graphql';
import Lockr from 'lockr';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import scrollIntoView from 'scroll-into-view-if-needed';
import useParseSavedSlugInfo from 'v2/core/services/savedSlug/useParseSavedSlugInfo';
import { AddExistingAnalysesToDashboardDialogContent } from '../AddExistingAnalysesToDashboardDialogContent';

interface DashboardAddContentMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  sx?: SxProps<Theme>;
  anchorEl: Element | (() => Element) | null;
  rowID?: string;
  cellInsertDirection: Direction;
  shouldShowNewOptions?: boolean;
}

export const DashboardAddContentMenu = ({
  sx,
  rowID,
  isOpen,
  onClose,
  anchorEl,
  anchorOrigin,
  transformOrigin,
  cellInsertDirection,
}: DashboardAddContentMenuProps) => {
  const { token: viewTokenFromURL } = useParseSavedSlugInfo();
  const [selectedAnalysisType, setSelectedAnalysisType] =
    useState<AnalysisUseCase>();
  const [shouldOpenQuantifyDrawer, setShouldOpenQuantifyDrawer] =
    useState(false);
  const [shouldOpenFeedDrawer, setShouldOpenFeedDrawer] = useState(false);

  const emptyQuantifyAnalysis = useMemo<AnalysisViewFragment>(
    () => ({
      ...INITIAL_ANALYSIS,
      title: 'New Quantify',
      useCase: AnalysisUseCase.Default,
      status: Status.Unarchived,
    }),
    [],
  );

  const emptyFeedAnalysis = useMemo<AnalysisViewFragment>(
    () => ({
      ...INITIAL_ANALYSIS,
      title: 'New Feed',
      useCase: AnalysisUseCase.Feed,
      status: Status.Unarchived,
    }),
    [],
  );

  const isMediaWidgetEnabled = useIsMediaWidgetEnabled();
  const { openModal } = useMuiModalContext();
  const navigate = useNavigate();
  const shouldShowInContextAnalysis = useShouldShowInContextAnalysis();

  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const context = useDashboardStore(({ visual }) => visual.context);
  const { upsertDashboard, loading: isCreatingDashboard } = useUpsertDashboard({
    shouldSkipRedirect: shouldShowInContextAnalysis,
  });
  const addTextWidget = useAddTextWidget({ rowID, cellInsertDirection });
  const addMediaWidget = useAddMediaWidget({ rowID, cellInsertDirection });

  const dashboardToAddTo = useMemo(() => {
    if (!dashboard) {
      return undefined;
    }

    return getDashboardListItemFromDashboardView(dashboard);
  }, [dashboard]);

  const scrollContentIntoView = useCallback((cellID: string | undefined) => {
    if (!cellID) {
      return;
    }

    setTimeout(() => {
      const cellElement = document.getElementById(cellID);
      if (cellElement) {
        scrollIntoView(cellElement, {
          scrollMode: 'if-needed',
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth',
        });
      }
    }, 1000);
  }, []);

  useEffect(() => {
    const cellIDToScrollTo: string = Lockr.get(DASHBOARD_ACTIVE_CELL_ID);

    if (cellIDToScrollTo) {
      scrollContentIntoView(cellIDToScrollTo);
    }
  }, [scrollContentIntoView]);

  const isEditable = useIsDashboardEditable();

  if (!dashboard || (!isEditable && dashboard.id)) {
    return null;
  }

  const editableDashboard = {
    ...dashboard,
    editToken: dashboard.editToken,
  };

  const addExistingAnalyses = () => {
    openModal({
      contentComponent: () => (
        <AddExistingAnalysesToDashboardDialogContent
          dashboard={editableDashboard}
          onAddAnalysis={(cell) => {
            scrollContentIntoView(cell?.id);
          }}
          rowID={rowID}
          cellInsertDirection={cellInsertDirection}
        />
      ),
      modalProps: {
        id: SAVED_ARTEFACTS_MODAL_ID,
        fullWidth: true,
        maxWidth: 'lg',
        PaperProps: {
          elevation: 3,
          sx: { width: '1000px', height: '600px', borderRadius: 2 },
        },
      },
    });
    onClose();
  };

  const addNewAnalysis = async (useCase: AnalysisUseCase) => {
    setSelectedAnalysisType(useCase);

    // Create a new dashboard if user is trying to add analysis to unsaved dashbaord
    const savedDashboard = dashboard?.id
      ? dashboard
      : await upsertDashboard({ dashboardProps: {} });

    if (!savedDashboard) {
      return;
    }

    if (shouldShowInContextAnalysis) {
      if (useCase === AnalysisUseCase.Default) {
        setShouldOpenQuantifyDrawer(true);
        onClose();
        return;
      }

      if (useCase === AnalysisUseCase.Feed) {
        setShouldOpenFeedDrawer(true);
        onClose();
        return;
      }
    }

    const route = getAddNewAnalysisToDashboardRoute({
      dashboard: {
        ...savedDashboard,
        title: savedDashboard.title,
        editToken: savedDashboard.editToken ?? '',
      },
      useCase,
      rowID,
      cellInsertDirection,
      context,
    });

    const dashboardName = savedDashboard.title;
    const dashboardEmoji = getEmojiFromDashboard(savedDashboard);

    navigate(route, {
      state: {
        dashboardName,
        dashboardEmoji,
      },
    });

    onClose();
  };

  return (
    <>
      <Menu
        sx={sx}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <MenuItem onClick={addExistingAnalyses}>
          <ListItemIcon>
            <BookmarkRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Saved Items</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => addNewAnalysis(AnalysisUseCase.Default)}
          disabled={isCreatingDashboard}
        >
          <ListItemIcon>
            {isCreatingDashboard &&
            selectedAnalysisType === AnalysisUseCase.Default ? (
              <CircularLoader size={24} />
            ) : (
              <InsightsRounded fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>New Quantify</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => addNewAnalysis(AnalysisUseCase.Feed)}
          disabled={isCreatingDashboard}
        >
          <ListItemIcon>
            {isCreatingDashboard &&
            selectedAnalysisType === AnalysisUseCase.Feed ? (
              <CircularLoader size={24} />
            ) : (
              <FeedIcon />
            )}
          </ListItemIcon>
          <ListItemText>New Feed</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            addTextWidget();
            onClose();
          }}
        >
          <ListItemIcon>
            <TextFieldsRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Text</ListItemText>
        </MenuItem>
        {isMediaWidgetEnabled ? (
          <MenuItem
            onClick={() => {
              addMediaWidget({
                imageLink: '',
              });
              onClose();
            }}
          >
            <ListItemIcon>
              <ImageRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Media</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>

      <QuantifyDrawerContainer
        analysis={emptyQuantifyAnalysis}
        isOpen={shouldOpenQuantifyDrawer}
        onClose={() => {
          setShouldOpenQuantifyDrawer(false);

          if (!viewTokenFromURL && dashboard.viewToken) {
            const dashboardRoute = `/${NAV_PATHS.DASHBOARD}/${dashboard.viewToken}`;
            navigate(dashboardRoute);
          }
        }}
        rowID={rowID || ''}
        dashboardToAddTo={dashboardToAddTo}
        cellInsertDirection={cellInsertDirection}
      />
      <FeedDrawerContainer
        analysis={emptyFeedAnalysis}
        isOpen={shouldOpenFeedDrawer}
        onClose={() => {
          setShouldOpenFeedDrawer(false);

          if (!viewTokenFromURL && dashboard.viewToken) {
            const dashboardRoute = `/${NAV_PATHS.DASHBOARD}/${dashboard.viewToken}`;
            navigate(dashboardRoute);
          }
        }}
        rowID={rowID || ''}
        dashboardToAddTo={dashboardToAddTo}
        cellInsertDirection={cellInsertDirection}
      />
    </>
  );
};
